import { createSlice } from '@reduxjs/toolkit';
import { notify } from 'components/Common/Toast';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { updateTableState } from 'redux/slices/common/appSlice';
import { fetchMembers } from 'services/membersApi';
import { fetchMemberInvitations, revokeMemberInvitation, type MemberInvitation } from 'services/memberInvitationsApi';
import { ParishMembersState } from 'types/community';
import type { Member } from 'types/members';
import { getProblemDetail } from 'api/v1Helpers';
import type { RootState } from 'redux/store';

type MembersState = ParishMembersState & {
    parishList: Member[];
    total: number;
    page: number;
    pageSize: number;
    loadedParishId: string | null;
    /** Parish id of the latest in-flight list request (race guard). */
    inflightParishId: string | null;
    invitations: MemberInvitation[];
    invitationsTotal: number;
    invitationsLoading: boolean;
    invitationsLoadedParishId: string | null;
    /** requestId of the latest dispatched invitations fetch — guards against an
     *  older overlapping request (e.g. tab-switch fetch vs. post-revoke refetch)
     *  resolving after a newer one and reverting the list. */
    invitationsRequestId: string | null;
};

const initialState: MembersState = {
    parishList: [],
    loading: false,
    error: null,
    total: 0,
    page: 1,
    pageSize: 20,
    loadedParishId: null,
    inflightParishId: null,
    invitations: [],
    invitationsTotal: 0,
    invitationsLoading: false,
    invitationsLoadedParishId: null,
    invitationsRequestId: null,
};

export interface FetchParishMembersPayload {
    parishId: string;
    searchText?: string;
}

export const fetchParishMembers = createAsyncThunkWrapper<
    { items: Member[]; total: number; page: number; pageSize: number; parishId: string } | null,
    FetchParishMembersPayload
>(
    'parishMembers/fetchParishMembers',
    async ({ parishId, searchText }, { getState }) => {
        if (!parishId) return null;
        try {
            // Read page/pageSize fresh from the shared table slice at execution time, not as a
            // caller-supplied argument — a value read from the component's render closure can be
            // stale on the first render after a parish switch (its page-reset dispatch lands one
            // render later) or on a fresh mount carrying over a leftover page number from a
            // different screen, and fetching against a stale value would show the wrong page's
            // data under a pagination control that already reads the corrected one.
            const { pageNumber = 0, pageSize = 20 } = (getState() as RootState).app.table;
            const page = pageNumber + 1;
            const res = await fetchMembers(parishId, {
                page,
                pageSize,
                search: searchText?.trim() || undefined,
            });
            // Ignore if selection moved before this response landed.
            if (parishSelectionMoved(getState, parishId)) return null;
            return { items: res.items, total: res.total, page: res.page, pageSize: res.pageSize, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load members' }
);

export const getMemberInvitations = createAsyncThunkWrapper<
    { items: MemberInvitation[]; total: number; parishId: string } | null,
    { parishId: string; status?: string }
>(
    'parishMembers/getMemberInvitations',
    async ({ parishId, status }, { getState }) => {
        if (!parishId) return null;
        try {
            // Server-paginated: read page/pageSize fresh from the shared table slice so the
            // request matches "Rows per page", and Community.tsx's fetch effect re-fires on
            // pageNumber changes too — Next/Prev now fetches the next page for real.
            const { pageNumber = 0, pageSize = 20 } = (getState() as RootState).app.table;
            const res = await fetchMemberInvitations(parishId, { page: pageNumber + 1, pageSize, status });
            if (parishSelectionMoved(getState, parishId)) return null;
            return { items: res.items, total: res.total, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load invitations' }
);

// The list is scoped to PENDING so it reads as an actionable queue (matches the
// staff-invitations precedent) and so a parish with a long revoked/accepted/expired
// history can never push a PENDING row past the API's page cap.
export const revokeMemberInvitationThunk = createAsyncThunkWrapper<{ parishId: string } | null, { parishId: string; invitationId: string }>(
    'parishMembers/revokeMemberInvitation',
    async ({ parishId, invitationId }, { getState, dispatch }) => {
        if (!parishId || !invitationId) return null;
        try {
            await revokeMemberInvitation(parishId, invitationId);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Invitation revoked', 'success');
            // Revoking the only/last row on the current page would otherwise leave the shared
            // table-pagination slice pointing past the end of the now-shorter client-sliced
            // list, rendering the empty-state branch even though earlier pages still have rows.
            // Dispatched before the refetch below (synchronous store update lands before that
            // thunk's own logic runs) so there's no extra request involved.
            const { pageNumber = 0, pageSize = 20 } = (getState() as RootState).app.table;
            if (pageNumber !== 0) {
                dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
            }
            dispatch(getMemberInvitations({ parishId, status: 'PENDING' }));
            return { parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to revoke invitation' }
);

const parishMemberSlice = createSlice({
    name: 'parishMembers',
    initialState,
    reducers: {
        clearParishMembers(state) {
            state.parishList = [];
            state.total = 0;
            state.loadedParishId = null;
            state.inflightParishId = null;
            state.invitations = [];
            state.invitationsTotal = 0;
            state.invitationsLoading = false;
            state.invitationsLoadedParishId = null;
            state.invitationsRequestId = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchParishMembers.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.parishList = [];
            state.inflightParishId = action.meta.arg.parishId;
        });
        builder.addCase(fetchParishMembers.fulfilled, (state, action) => {
            if (!action.payload) {
                // Stale or cancelled — do not clobber a newer in-flight parish.
                if (state.inflightParishId === action.meta.arg.parishId) {
                    state.loading = false;
                }
                return;
            }
            const { items, total, page, pageSize, parishId } = action.payload;
            // Drop responses that are not for the latest requested parish.
            if (parishId !== state.inflightParishId) return;
            state.loading = false;
            state.parishList = items;
            state.total = total;
            state.page = page;
            state.pageSize = pageSize;
            state.loadedParishId = parishId;
        });
        builder.addCase(fetchParishMembers.rejected, (state, action) => {
            if (state.inflightParishId === action.meta.arg.parishId) {
                state.loading = false;
                state.error = (action.payload as string) || 'Failed to load members';
            }
        });

        builder.addCase(getMemberInvitations.pending, (state, action) => {
            state.invitationsLoading = true;
            // Only blank the list on a parish switch — a same-parish refetch (e.g. after
            // revoke) keeps showing the current rows under the loading overlay instead of
            // flashing empty then repainting.
            if (state.invitationsLoadedParishId !== action.meta.arg.parishId) {
                state.invitations = [];
            }
            state.invitationsRequestId = action.meta.requestId;
        });
        builder.addCase(getMemberInvitations.fulfilled, (state, action) => {
            // A newer request (parish switch, or a refetch fired while this one was still in
            // flight) has already superseded this response — drop it so it can't revert
            // state.invitations to older data.
            if (action.meta.requestId !== state.invitationsRequestId) return;
            state.invitationsLoading = false;
            if (!action.payload) return; // parish moved mid-flight; nothing to apply
            state.invitations = action.payload.items;
            state.invitationsTotal = action.payload.total;
            state.invitationsLoadedParishId = action.payload.parishId;
        });
        builder.addCase(getMemberInvitations.rejected, (state, action) => {
            if (action.meta.requestId !== state.invitationsRequestId) return;
            state.invitationsLoading = false;
        });
    },
});

export const { clearParishMembers } = parishMemberSlice.actions;
export default parishMemberSlice.reducer;
